import { forwardRef, type ComponentPropsWithoutRef, type CSSProperties, type ReactNode } from 'react';
import { UnstyledButton } from '@mantine/core';
import { colorWithAlpha } from '@/utils/colorWithAlpha';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import classes from './EditorChipButton.module.css';

type EditorChipButtonProps = ComponentPropsWithoutRef<'button'> & {
  accentColor?: string;
  variant?: 'default' | 'danger' | 'primary';
  leftIcon?: ReactNode;
  children?: ReactNode;
};

function getEditorChipButtonStyleVars(accentColor: string): CSSProperties {
  return {
    '--editor-btn-accent': accentColor,
    '--editor-btn-border': colorWithAlpha(accentColor, 0.32),
    '--editor-btn-hover-bg': colorWithAlpha(accentColor, 0.06),
    '--editor-btn-hover-border': colorWithAlpha(accentColor, 0.45),
  } as CSSProperties;
}

export const EditorChipButton = forwardRef<HTMLButtonElement, EditorChipButtonProps>(
  function EditorChipButton(
    {
      accentColor = DEFAULT_QR_CODE_COLOR,
      variant = 'default',
      leftIcon,
      children,
      className,
      style,
      type = 'button',
      disabled,
      ...rest
    },
    ref
  ) {
    const showLabel = children != null && children !== false;

    return (
      <UnstyledButton
        ref={ref}
        type={type}
        disabled={disabled}
        className={`${classes.button} ${variant === 'danger' ? classes.buttonDanger : ''}${variant === 'primary' ? ` ${classes.buttonPrimary}` : ''}${className ? ` ${className}` : ''}`}
        style={{
          ...((variant === 'default' || variant === 'primary')
            ? getEditorChipButtonStyleVars(accentColor)
            : undefined),
          ...style,
        }}
        {...rest}
      >
        <span className={classes.inner}>
          {leftIcon ? <span className={classes.icon}>{leftIcon}</span> : null}
          {showLabel ? <span className={classes.label}>{children}</span> : null}
        </span>
      </UnstyledButton>
    );
  }
);
